import React, {useEffect, useState, useRef, useMemo, useCallback} from "react";
import PropTypes from "prop-types";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import styled from "styled-components";

// Utils
import {toTitleCase, exists} from "../../utils/helpers.js";
import {FIELD, COMPONENT, GROUP, fieldValidationSchema, getFields} from "../../utils/builder.js";
import {getFieldLinkDefaults, resolveFieldParentName} from "./helpers.js";

// Components
import FieldBuilder from "../../components/FieldBuilder.js";
import SearchSelect from "../../components/SearchSelect.js";
import ElementFieldHelp from "../general/ElementFieldHelp.js";
import InputSelect from "../../components/form/InputSelect.js";
import Modal from "../../components/Modal.js";
import ModalCheckCondition from "./ModalCheckConditions.js";

// Style
import {colors, pad} from "../../style/components/variables.js";
import {
  HeadingCenter,
  Button,
  FormGroup,
  FormField,
  Form,
  ButtonFull,
  Label,
  Inline,
  Text
} from "../../style/components/general.js";
import voice from "../../style/components/typography.js";

const ModalEditElement = ({
  visible = false,
  setVisible,
  hasBackButton = false,
  goBack,
  targetElement,
  persistEdit,
  builder,
  otherBuilder,
  state,
  isState = false,
  units = [],
  templateLabel,
  templates,
  editing = false
}) => {
  const [restrictedError, setRestrictedError] = useState(null);
  const [acceptableError, setAcceptableError] = useState(null);
  const [rangeError, setRangeError] = useState(null);
  const [unlinkTemplate, setUnlinkTemplate] = useState(!targetElement.templateId);
  const [linkToTemplate, setLinkToTemplate] = useState(null);
  const [templateResults, setTemplateResults] = useState(templates);
  const [affectedFields, setAffectedFields] = useState([]);
  // Modals
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [showModalCheckConditions, setShowModalCheckConditions] = useState(false);

  const stored = useRef();

  const hasChildFields = useMemo(() => {
    const {children} = targetElement;
    const fieldHelp = children?.filter(child => builder.byId[child]?.element === FIELD);
    return fieldHelp?.length > 0;
  }, [builder, targetElement]);

  const defaultElement = useMemo(() => {
    const {help: allHelp, children, element} = targetElement;

    let generalHelp = null;
    let fieldHelp = null;

    if (element === FIELD && targetElement.parentName && targetElement.parentName in builder.byId) {
      const parentElement = builder.byId[targetElement.parentName];
      const help = parentElement?.help?.filter(h => h.id === targetElement.name);
      if (help?.length > 0) generalHelp = help[0].value;
    } else {
      const fieldHelpItems = allHelp?.filter(({id}) => !!id);
      generalHelp = allHelp?.filter(({id}) => !id);

      fieldHelp = children
        ?.filter(
          child =>
            builder.byId[child]?.element === FIELD &&
            !builder.byId[child]?.shared &&
            !builder.byId[child]?.deleted &&
            !builder.byId[child]?.stale
        )
        .map(child => {
          const found = fieldHelpItems?.filter(({id}) => id === child);
          if (found?.length > 0) return {...found[0], enabled: true};
          return {key: builder.byId[child].label, value: null, id: child, enabled: false};
        });
    }

    const fallbackHelp = element === FIELD ? "" : [{key: null, value: null}];

    if (generalHelp?.length === 0) generalHelp = fallbackHelp;

    if (element === FIELD) {
      const {linkedGroup, linkedComponent} = getFieldLinkDefaults(builder, targetElement);
      return {
        ...targetElement,
        label: targetElement.label || "",
        hasLimits: targetElement.hasLimits || false,
        limits: targetElement.limits || [{}],
        help: generalHelp || fallbackHelp,
        linkedGroup,
        linkedComponent
      };
    }

    return {
      ...targetElement,
      help: generalHelp || fallbackHelp,
      fieldHelp
    };
  }, [builder, targetElement]);

  delete targetElement.linkedGroup;
  delete targetElement.linkedComponent;

  const schema =
    targetElement.element === FIELD
      ? fieldValidationSchema(targetElement.parentName, builder)
      : yup.object().shape({
          fieldHelp: yup
            .mixed()
            .nullable()
            .when("hasHelp", {
              is: val => !!val,
              then: () =>
                yup
                  .array()
                  .of(yup.object())
                  .test({
                    test: val =>
                      !hasChildFields ||
                      (Array.isArray(val) && val.every(({enabled, value}) => !enabled || !!value)),
                    message: "Please provide field help or disable unused help items."
                  })
            })
        });

  const form = useForm({
    defaultValues: defaultElement,
    resolver: yupResolver(schema)
  });
  const {watch, handleSubmit, reset, setValue} = form;

  const watchElement = watch();
  const watchLinkedGroup = watch("linkedGroup");
  const watchLinkedComponent = watch("linkedComponent");
  const watchHasARange = watch("hasARange");
  const watchAMin = watch("aMin");
  const watchAMax = watch("aMax");
  const watchHasRange = watch("hasRange");
  const watchMin = watch("min");
  const watchMax = watch("max");

  useEffect(() => {
    setRestrictedError(null);
  }, [watchHasRange, watchMin, watchMax]);

  useEffect(() => {
    setAcceptableError(null);
  }, [watchHasARange, watchAMin, watchAMax]);

  useEffect(() => {
    const grandparent =
      watchLinkedComponent && watchLinkedComponent in builder.byId
        ? builder.byId[watchLinkedComponent].parentName
        : null;
    if (
      watchLinkedGroup &&
      watchLinkedGroup !== "" &&
      watchLinkedGroup !== targetElement.parentName &&
      (!grandparent || watchLinkedGroup !== grandparent)
    )
      setValue("linkedComponent", null);

    if (watchLinkedGroup === "") setValue("linkedComponent", null);
  }, [watchLinkedGroup, setValue, targetElement, builder, watchLinkedComponent]);

  const getLinkedGroup = (grandparent, linkedGroups) => {
    const groups = linkedGroups.map(item => item.name);
    if (groups.includes(targetElement.parentName)) return targetElement.parentName;
    if (grandparent && groups.includes(grandparent)) return grandparent;
    return null;
  };

  const renderFieldOptions = () => {
    const linkedGroups = builder.allIds
      .map(id => builder.byId[id])
      .filter(el => el?.element === GROUP && el?.toggle);
    const grandparent =
      targetElement.parentName in builder.byId
        ? builder.byId[targetElement.parentName].parentName
        : null;

    const currentGroup = getLinkedGroup(grandparent, linkedGroups);
    const linkedComponents =
      watchLinkedGroup && builder.byId[watchLinkedGroup]?.children
        ? builder.byId[watchLinkedGroup].children
            .map(id => builder.byId[id])
            .filter(el => el?.element === COMPONENT && el?.toggle)
        : [];
    return (
      <>
        {linkedGroups.length > 0 && (
          <FormField>
            <InputSelect
              testId="edit.linkedGroup"
              name="linkedGroup"
              options={linkedGroups}
              label="Linked Group"
              placeholder="Link to Group..."
              defaultValue={currentGroup}
            />
          </FormField>
        )}
        {linkedComponents.length > 0 && (
          <FormField>
            <InputSelect
              testId="edit.linkedComponent"
              name="linkedComponent"
              options={linkedComponents}
              placeholder="Link to Component..."
              label="Linked Component"
              defaultValue={
                linkedComponents.map(item => item.name).includes(targetElement.parentName)
                  ? targetElement.parentName
                  : null
              }
            />
          </FormField>
        )}
      </>
    );
  };

  const renderComponentOptions = () => (
    <>
      {(templateLabel || linkToTemplate) && !unlinkTemplate && (
        <FormField>
          <Inline>
            <Label bold>Linked Template: </Label>
            <Component>{linkToTemplate ? linkToTemplate.label : templateLabel}</Component>
            <ButtonLink
              type="button"
              onClick={() => {
                setLinkToTemplate(null);
                setUnlinkTemplate(true);
              }}>
              Unlink
            </ButtonLink>
          </Inline>
        </FormField>
      )}
      {unlinkTemplate && templates && (
        <FormField>
          <Inline>
            <SearchSelect
              testId="edit.linkedTemplate"
              name="linkedTemplate"
              results={templateResults}
              setResults={setTemplateResults}
              search={query => {
                if (!query) setTemplateResults(templates);
                setTemplateResults(
                  templates.filter(template =>
                    template.label.toLowerCase().includes(query.toLowerCase())
                  )
                );
              }}
              label="Linked Template"
              add={selection => {
                setUnlinkTemplate(false);
                setLinkToTemplate(selection);
              }}
              placeholder="Find Template..."
            />
          </Inline>
        </FormField>
      )}
    </>
  );

  useEffect(() => {
    if (visible) reset(defaultElement);
    else reset();
  }, [visible, defaultElement, reset]);

  const getMap = useCallback((original, updated) => {
    const {options: oOptions, on: oOn, off: oOff} = original;
    const {options: uOptions, on: uOn, off: uOff} = updated;

    const map = {
      [oOn]: uOn,
      [oOff]: uOff
    };

    const len = Math.min(oOptions?.length || 0, uOptions?.length || 0);
    for (let i = 0; i < len; i++) {
      map[oOptions[i].option] = uOptions[i].option;
    }

    return map;
  }, []);

  const getAffectedFields = useCallback(
    (original, updated) => {
      const map = getMap(original, updated);

      // Precompute the set of affected options for faster lookup
      const affectedOptions = new Set(
        original.options?.filter(({option}) => map[option] !== option).map(({option}) => option)
      );

      // Precompute affected "on" and "off" values
      const affectedOn = map[original.on] !== original.on ? original.on : null;
      const affectedOff = map[original.off] !== original.off ? original.off : null;

      return getFields(builder.allIds, builder.byId).filter(({condition}) => {
        if (!condition?.list) return false;

        return condition.list.some(({depends, compare}) => {
          if (depends !== original.name) return false;

          const compareArray = Array.isArray(compare) ? compare : [compare];
          return compareArray.some(
            value =>
              affectedOptions.has(value) ||
              (value === affectedOn && affectedOn !== null) ||
              (value === affectedOff && affectedOff !== null)
          );
        });
      });
    },
    [builder, getMap]
  );

  const handleSave = values => {
    let shouldReturn = false;
    if (values.hasARange && !exists(values.aMin) && !exists(values.aMax)) {
      setAcceptableError("Either a min or max must be provided.");
      shouldReturn = true;
    } else setAcceptableError(null);

    if (values.hasRange && !exists(values.min) && !exists(values.max)) {
      setRestrictedError("Either a min or max must be provided.");
      shouldReturn = true;
    } else setRestrictedError(null);

    if (values.type === "range" && !(exists(values.rangeMin) && exists(values.rangeMax))) {
      setRangeError("Min and max must be provided.");
      shouldReturn = true;
    } else setRangeError(null);

    if (shouldReturn) return;

    if (values?.units?.includes("Other: ")) values.units = values.units.replace("Other: ", "");

    const {linkedComponent: newParentComponent, linkedGroup: newParentGroup} = values;
    const elementParent = resolveFieldParentName(builder, targetElement, {
      linkedComponent: newParentComponent,
      linkedGroup: newParentGroup
    });

    let newHelp = values.help;
    if (targetElement.element !== FIELD) {
      const {help: elementHelp, fieldHelp} = values;
      if (elementHelp) newHelp = [...elementHelp.filter(({key, value}) => !!key && !!value)];
      if (fieldHelp)
        newHelp = [
          ...newHelp,
          ...fieldHelp
            .filter(({key, value}) => !!key && !!value)
            .map(item => {
              delete item.enabled;
              return item;
            })
        ];
    }

    const updated = {
      ...targetElement,
      ...values,
      parentName: elementParent,
      help: newHelp
    };

    if (updated.linkedComponent !== undefined) delete updated.linkedComponent;
    if (updated.linkedGroup !== undefined) delete updated.linkedGroup;
    if (updated.phone && updated.phone.number === "") delete updated.phone;
    if (unlinkTemplate && updated.templateId !== undefined) delete updated.templateId;
    if (linkToTemplate) updated.templateId = linkToTemplate.id;
    delete updated.fieldHelp;

    const f =
      targetElement.element === FIELD ? getAffectedFields(targetElement, updated) : undefined;

    if (f?.length > 0) {
      stored.current = {updated};
      setAffectedFields(f);
      setShowModalCheckConditions(true);
    } else if (editing && values.type !== targetElement.type) {
      stored.current = {updated};
      setShowModalConfirm(true);
    } else persistEdit(updated);
  };

  if (showModalConfirm)
    return (
      <Modal visible={showModalConfirm} setVisible={setShowModalConfirm} allowClose={false}>
        <Error>Warning!</Error>
        <Text>
          Changing a field&apos;s type will detach previous responses and this field will be treated
          as a new field. Are you sure you want to proceed?
        </Text>
        <br />
        <Inline>
          <Button
            type="button"
            onClick={() => {
              const {updated} = stored.current;
              persistEdit(updated);
            }}>
            Confirm
          </Button>
          <Button type="button" onClick={() => setShowModalConfirm(false)}>
            Cancel
          </Button>
        </Inline>
      </Modal>
    );

  if (showModalCheckConditions)
    return (
      <ModalCheckCondition
        visible={showModalCheckConditions}
        setVisible={setShowModalCheckConditions}
        builder={builder}
        target={stored.current.updated}
        original={targetElement}
        affectedFields={affectedFields}
        handleSave={updatedBuilder => {
          const {updated} = stored.current;
          persistEdit(updated, updatedBuilder);
        }}
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible} hasBackButton={hasBackButton} goBack={goBack}>
      <ModalHeading element={targetElement.element}>
        Edit {toTitleCase(targetElement.element)}: {targetElement.label}
      </ModalHeading>

      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(handleSave)} noValidate>
          <FormGroup>
            {targetElement.element === COMPONENT && renderComponentOptions()}
            {targetElement.element === FIELD && (
              <>
                {renderFieldOptions()}
                <FieldBuilder
                  field={watchElement}
                  editElement
                  restrictedError={restrictedError}
                  acceptableError={acceptableError}
                  rangeError={rangeError}
                  units={units}
                />
              </>
            )}
          </FormGroup>

          <FormGroup>
            {hasChildFields && <ElementFieldHelp name="fieldHelp" label="FIELD HELP" />}
          </FormGroup>

          {!targetElement.shared &&
            state &&
            otherBuilder &&
            otherBuilder.byId[targetElement.name] &&
            !otherBuilder.byId[targetElement.name].deleted && (
              <WarningBody>
                <Warning>Warning!&nbsp;</Warning>This field exists on both states. Changes to this
                field will also be applied to the corresponding field on the{" "}
                <strong>{isState ? "DEFAULT" : state.name}</strong> state.
              </WarningBody>
            )}
          <ButtonFull type="submit" data-testid="edit.save">
            Save
          </ButtonFull>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalEditElement.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func,
  targetElement: PropTypes.objectOf(PropTypes.any).isRequired,
  persistEdit: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  otherBuilder: PropTypes.objectOf(PropTypes.any),
  state: PropTypes.objectOf(PropTypes.any),
  isState: PropTypes.bool,
  units: PropTypes.arrayOf(PropTypes.string),
  templateLabel: PropTypes.string,
  templates: PropTypes.arrayOf(PropTypes.any),
  editing: PropTypes.bool
};

// Style Overrides
const ModalHeading = styled(HeadingCenter)`
  color: ${({element, theme}) => (element === GROUP ? theme.group : theme.component)};
`;

const Component = styled.span`
  margin-top: -${pad / 2}px;
  color: ${({theme}) => theme.component};
`;

const ButtonLink = styled(Button)`
  margin-top: -${pad / 2}px;
`;

const Error = styled(HeadingCenter)`
  color: ${({theme}) => theme.error};
`;

const Warning = styled.strong`
  color: ${colors.yellow};
`;

const WarningBody = styled.span`
  ${voice.quiet};
  margin-bottom: ${pad}px;
`;

export default ModalEditElement;
